import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';

import { api } from '@/shared/api/client';
import { useRedo, useUndo } from '@/shared/api/mutations';
import { useI18nStore } from '@/shared/i18n';
import { useUiStore } from '@/shared/store/ui-store';
import { useViewerStore } from '@/shared/store/viewer-store';
import { OPEN_COMMAND_PALETTE_EVENT } from '@/features/command-palette/events';
import { OPEN_SHORTCUTS_EVENT } from '@/features/shortcuts/events';
import { openImageKitchenWorkflow } from '@/features/image-kitchen/workflow-event';
import { WorkbenchCommandProvider, type WorkbenchContextValue } from './command-context';
import { WORKBENCH_COMMAND_BY_ID, WORKBENCH_COMMANDS } from './registry';
import { openViewportControls } from './viewport-controls';
import { openProjectSurface } from './project-surfaces';
import type {
  ResolvedUiCommand,
  UiCommand,
  ViewerAction,
  WorkbenchCapability,
  WorkbenchRuntimeAdapters,
} from './types';

type ViewerActionOwner = Pick<ReturnType<typeof useViewerStore.getState>,
  'setProjection' | 'setRenderMode' | 'setLightPreset' | 'setDimensionScope'
  | 'setExplode' | 'setSection' | 'isolateSelected' | 'hideSelected' | 'showAll'
  | 'openAllPanels' | 'closeAllPanels'>;

/** Closed execution owner for the Viewer command surface. */
export function executeViewerAction(viewer: ViewerActionOwner, action: ViewerAction): void {
  switch (action) {
    case 'projection:perspective': viewer.setProjection('perspective'); break;
    case 'projection:iso': viewer.setProjection('iso'); break;
    case 'projection:top': viewer.setProjection('top'); break;
    case 'projection:front': viewer.setProjection('front'); break;
    case 'projection:side': viewer.setProjection('side'); break;
    case 'render:solid': viewer.setRenderMode('solid'); break;
    case 'render:wireframe': viewer.setRenderMode('wireframe'); break;
    case 'render:xray': viewer.setRenderMode('xray'); break;
    case 'light:studio': viewer.setLightPreset('studio'); break;
    case 'light:showroom': viewer.setLightPreset('showroom'); break;
    case 'light:daylight': viewer.setLightPreset('daylight'); break;
    case 'light:photo': viewer.setLightPreset('photo'); break;
    case 'dimension:off': viewer.setDimensionScope('off'); break;
    case 'dimension:overall': viewer.setDimensionScope('overall'); break;
    case 'dimension:inline': viewer.setDimensionScope('inline'); break;
    case 'explode:on': viewer.setExplode(0.4); break;
    case 'explode:off': viewer.setExplode(0); break;
    case 'section:z': viewer.setSection('z', 0.5); break;
    case 'selection:isolate': viewer.isolateSelected(); break;
    case 'selection:hide': viewer.hideSelected(); break;
    case 'selection:show-all': viewer.showAll(); break;
    case 'panels:open-all': viewer.openAllPanels(); break;
    case 'panels:close-all': viewer.closeAllPanels(); break;
    default: {
      const unreachable: never = action;
      throw new Error(`Unknown viewer action: ${String(unreachable)}`);
    }
  }
}

function disabledReason(command: UiCommand, runtime: WorkbenchRuntimeAdapters): string | undefined {
  if (command.requiresActiveModel && !runtime.activeModelId) return 'Сначала выберите модель';
  if (command.destination.kind === 'capability' && !runtime.capabilities[command.destination.value]) {
    return 'Действие не подключено в этом рабочем месте';
  }
  return undefined;
}

export function resolveWorkbenchCommand(
  command: UiCommand,
  runtime: WorkbenchRuntimeAdapters,
): ResolvedUiCommand {
  const reason = disabledReason(command, runtime);
  return {
    ...command,
    enabled: !reason,
    disabledReason: reason,
    execute: () => {
      if (reason) return;
      const destination = command.destination;
      if (destination.kind === 'workspace') runtime.setWorkspace(destination.value);
      else if (destination.kind === 'scene-mode') {
        runtime.setWorkspace('scene');
        runtime.setAppMode(destination.value);
      } else if (destination.kind === 'left-rail') {
        runtime.setWorkspace('scene');
        runtime.openLeft(destination.value);
      } else if (destination.kind === 'right-rail') {
        runtime.setWorkspace('scene');
        runtime.openRight(destination.value);
      } else if (destination.kind === 'tech-section') {
        runtime.setWorkspace('tech');
        runtime.setTechSection(destination.value);
      } else if (destination.kind === 'viewer-action') {
        runtime.setWorkspace('scene');
        runtime.setAppMode('scene');
        runtime.runViewerAction(destination.value);
      } else if (destination.kind === 'capability') {
        return runtime.capabilities[destination.value]?.();
      } else {
        /*
         * Команда модуля, дошедшая до исполнителя ВЬЮЕРА, — не «нечего
         * делать», а расхождение договора: её обязан был исполнить тот
         * `resolve`, который её и объявил. Молчаливый `?.()` превратил бы это
         * в кнопку, которая нажимается и ничего не делает, — самый дорогой
         * род отказа, потому что его не видно.
         */
        throw new Error(`WORKBENCH_MODULE_COMMAND_HAS_NO_OWNER_HERE: ${destination.value}`);
      }
    },
  };
}

/** Ниже этой ширины две панели вместе съедают сцену — там они и гасят друг друга. */
export const RAIL_EXCLUSIVE_MAX_WIDTH = 980;

export function railsAreExclusive(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < RAIL_EXCLUSIVE_MAX_WIDTH;
}

export function WorkbenchRuntimeProvider({
  children,
  onStartTour,
}: {
  children: ReactNode;
  onStartTour: () => void;
}) {
  const activeModelId = useViewerStore((state) => state.activeModelId);
  const setActiveModel = useViewerStore((state) => state.setActiveModel);
  const setWorkspace = useUiStore((state) => state.setWorkspace);
  const setAppMode = useUiStore((state) => state.setAppMode);
  const setTechSection = useUiStore((state) => state.setTechSection);
  const rawOpenLeft = useUiStore((state) => state.openLeft);
  const rawOpenRight = useUiStore((state) => state.openRight);
  const closeLeft = useUiStore((state) => state.closeLeft);
  const closeRight = useUiStore((state) => state.closeRight);
  const openScenePipeline = useUiStore((state) => state.openScenePipeline);
  const toggleTheme = useUiStore((state) => state.toggleTheme);
  const locale = useI18nStore((state) => state.locale);
  const setLocale = useI18nStore((state) => state.setLocale);
  const undo = useUndo(activeModelId);
  const redo = useRedo(activeModelId);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  /* Панели-соседи гасят друг друга ТОЛЬКО в узком окне.
     Вставка модуля живёт в левой панели (Библиотека / Каталог / Шаблоны / Конструктор), а
     единственная форма габарита и расстановки — ModuleEditHud — портируется в правую
     («Свойства»). Безусловное гашение делало заявленный порядок работы невозможным:
     открыл Шаблоны — правая закрылась вместе с полями Ш/В/Г и X/Y/Z; добавил корпус — он
     стал активным, а его форма в этот же момент исчезла из DOM. На широком экране обе
     панели помещаются (раскладка это умеет), и гасить их незачем. */
  const openLeft = useCallback((rail: Parameters<typeof rawOpenLeft>[0]) => {
    if (railsAreExclusive()) closeRight();
    rawOpenLeft(rail);
  }, [closeRight, rawOpenLeft]);
  const openRight = useCallback((rail: Parameters<typeof rawOpenRight>[0]) => {
    if (railsAreExclusive()) closeLeft();
    rawOpenRight(rail);
  }, [closeLeft, rawOpenRight]);

  const shareProject = useCallback(async () => {
    if (!activeModelId) return;
    try {
      const created = await api.createModelShare(activeModelId);
      const url = `${window.location.origin}/#share=${encodeURIComponent(created.token)}`;
      await navigator.clipboard.writeText(url);
      toast.success('Ссылка для просмотра скопирована', {
        description: `Действует до ${new Date(created.expiresAt).toLocaleDateString('ru-RU')}.`,
      });
    } catch (error) {
      toast.error(`Не удалось создать ссылку: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [activeModelId]);

  const exportProject = useCallback(async () => {
    if (!activeModelId) return;
    try {
      // P2-аудит 10.10: единственный raw fetch в топ-30 — ответ не JSON,
      // а .holzcom-блоб; общий api.* (request → response.json) для этого не
      // предназначен, поэтому здесь собственный fetch с response.blob().
      const response = await fetch(`/api/models/${encodeURIComponent(activeModelId)}/export.holzcom`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${activeModelId}.holzcom`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('Проект экспортирован');
    } catch (error) {
      toast.error(`Не удалось экспортировать проект: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [activeModelId]);

  const importProject = useCallback(async (file: File) => {
    try {
      const response = await fetch('/api/projects/import.holzcom', {
        method: 'POST',
        headers: { 'Content-Type': 'application/zip' },
        body: file,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || `HTTP ${response.status}`);
      }
      const payload = await response.json();
      setActiveModel(payload.modelId);
      toast.success(`Проект импортирован: ${payload.modelId}`);
      if (payload.warnings?.length) toast.warning(payload.warnings.slice(0, 2).join(' · '));
    } catch (error) {
      toast.error(`Не удалось импортировать проект: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [setActiveModel]);

  const showViewportControls = useCallback((group: Parameters<typeof openViewportControls>[0]) => {
    setWorkspace('scene');
    setAppMode('scene');
    openViewportControls(group);
  }, [setAppMode, setWorkspace]);

  const runViewerAction = useCallback<WorkbenchRuntimeAdapters['runViewerAction']>((action) => {
    executeViewerAction(useViewerStore.getState(), action);
  }, []);

  const capabilities = useMemo<Partial<Record<WorkbenchCapability, () => void | Promise<void>>>>(() => ({
    'open-command-palette': () => { window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE_EVENT)); },
    'open-shortcuts': () => { window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT)); },
    'open-model-picker': () => { setWorkspace('scene'); openLeft('models'); },
    'open-scene-pipeline': openScenePipeline,
    'open-image-kitchen-upload': () => openImageKitchenWorkflow('upload'),
    'open-image-kitchen-graph1': () => openImageKitchenWorkflow('graph1'),
    'open-image-kitchen-graph2': () => openImageKitchenWorkflow('graph2'),
    'open-image-kitchen-model': () => openImageKitchenWorkflow('model'),
    'open-image-kitchen-smeta': () => openImageKitchenWorkflow('smeta'),
    'share-project': shareProject,
    'export-project': exportProject,
    'import-project': () => importInputRef.current?.click(),
    'toggle-locale': () => setLocale(locale === 'ru' ? 'en' : 'ru'),
    'toggle-appearance': toggleTheme,
    'start-tour': onStartTour,
    undo: () => undo.mutate(),
    redo: () => redo.mutate(),
    logout: () => { window.dispatchEvent(new Event('holzcom-logout')); },
    'open-project-menu': () => openProjectSurface('menu'),
    'project-diagnostics': () => openProjectSurface('diagnostics'),
    'viewport-controls': () => showViewportControls('projection'),
    'viewport-layers': () => showViewportControls('layers'),
    'viewport-dimensions': () => showViewportControls('dimensions'),
    'viewport-environment': () => showViewportControls('environment'),
    'open-recognition': () => { window.history.pushState(null, '', '/recognition-arm'); },
    'open-buildings': () => { window.history.pushState(null, '', '/buildings'); },
    'open-event-location': () => { window.history.pushState(null, '', '/event-location'); },
    'open-mesh-studio': () => { window.history.pushState(null, '', '/mesh-studio'); },
    'module-actions': () => { setWorkspace('scene'); openRight('properties'); },
  }), [exportProject, showViewportControls, locale, onStartTour, openLeft, openRight, openScenePipeline, redo, setLocale, setWorkspace, shareProject, toggleTheme, undo]);

  const runtime = useMemo<WorkbenchRuntimeAdapters>(() => ({
    activeModelId,
    setWorkspace,
    setAppMode,
    setTechSection,
    openLeft,
    openRight,
    runViewerAction,
    capabilities,
  }), [activeModelId, capabilities, openLeft, openRight, runViewerAction, setAppMode, setTechSection, setWorkspace]);

  const resolve = useCallback((command: UiCommand) => resolveWorkbenchCommand(command, runtime), [runtime]);
  const execute = useCallback((commandId: string) => {
    const command = WORKBENCH_COMMAND_BY_ID.get(commandId);
    if (!command) throw new Error(`Unknown workbench command: ${commandId}`);
    return resolveWorkbenchCommand(command, runtime).execute();
  }, [runtime]);

  const value = useMemo<WorkbenchContextValue>(() => ({
    commands: WORKBENCH_COMMANDS,
    resolve,
    execute,
  }), [execute, resolve]);

  return (
    <WorkbenchCommandProvider value={value}>
      {children}
      <input
        ref={importInputRef}
        type="file"
        accept=".holzcom,application/zip"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void importProject(file);
          event.target.value = '';
        }}
      />
    </WorkbenchCommandProvider>
  );
}

export { useWorkbenchRuntime } from './command-context';
